import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, TrendingUp, TrendingDown, Plus, Filter, X, Package, Info, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DateRangePicker from '../../components/common/DateRangePicker';
import { getPeriodRange } from '../../components/common/PeriodPills';
import InputMontant from '../../components/common/InputMontant';
import { getDeviseCourante } from '../../utils/format';

function formatGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}

const CANAUX_LABELS = {
  magasin:         { label: 'Magasin',         color: 'text-blue-400' },
  terrain:         { label: 'Terrain',          color: 'text-purple-400' },
  livraison:       { label: 'Livraison',        color: 'text-indigo-400' },
  approvisionnement: { label: 'Appro.',         color: 'text-orange-400' },
  depense_diverse: { label: 'Dépense diverse',  color: 'text-red-400' },
  retour_vendeur:  { label: 'Retour vendeur',   color: 'text-green-400' },
  remboursement:   { label: 'Remboursement',    color: 'text-yellow-400' },
};

function CanalBadge({ canal }) {
  const c = CANAUX_LABELS[canal] || { label: canal, color: 'text-gray-400' };
  return <span className={`text-xs font-medium ${c.color}`}>{c.label}</span>;
}

// ── Modal dépense diverse ─────────────────────────────────────────────────────
function ModalDepense({ onClose, onSuccess }) {
  const [desc, setDesc]     = useState('');
  const [montant, setMontant] = useState('');
  const [loading, setLoading] = useState(false);
  // Boutique concernée par la dépense (obligatoire si plusieurs boutiques).
  const [boutiques, setBoutiques] = useState([]);
  const [boutiqueId, setBoutiqueId] = useState('');

  useEffect(() => {
    api.get('/boutiques')
      .then(r => {
        const actives = (r.data || []).filter(b => b.statut === 'actif');
        setBoutiques(actives);
        const principale = actives.find(b => b.nom === 'Principale') || actives[0];
        if (principale) setBoutiqueId(principale.id);
      })
      .catch(() => {});
  }, []);

  async function soumettre() {
    if (!desc.trim() || !montant || parseFloat(montant) <= 0) {
      return toast.error('Description et montant requis');
    }
    if (boutiques.length > 1 && !boutiqueId) {
      return toast.error('Choisis la boutique concernée par cette dépense');
    }
    setLoading(true);
    try {
      await api.post('/caisse/depense', {
        description: desc,
        montant: parseFloat(montant),
        boutique_id: boutiqueId || undefined,
      });
      toast.success('Dépense enregistrée');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Enregistrer une dépense</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {boutiques.length > 1 && (
            <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-xl p-3">
              <label className="block text-sm font-semibold text-yellow-400 mb-1.5">
                Boutique concernée *
              </label>
              <select value={boutiqueId} onChange={e => setBoutiqueId(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500">
                <option value="">— Choisir la boutique —</option>
                {boutiques.map(b => (
                  <option key={b.id} value={b.id}>{b.nom}{b.ville ? ` — ${b.ville}` : ''}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">La dépense sera imputée à cette boutique.</p>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Description</label>
            <input type="text" value={desc} onChange={e => setDesc(e.target.value)}
              placeholder="Ex : Achat fournitures, carburant…"
              className="w-full bg-gray-800 border border-gray-600 rounded-xl px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Montant ({getDeviseCourante()})</label>
            <InputMontant value={montant} onChange={setMontant}
              placeholder="0"
              className="w-full bg-gray-800 border border-gray-600 rounded-xl px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500" />
          </div>
          <button onClick={soumettre} disabled={loading}
            className="w-full py-3 bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-bold rounded-xl flex items-center justify-center gap-2">
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><TrendingDown size={16} /> Enregistrer la dépense</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page Caisse ───────────────────────────────────────────────────────────────
export default function Caisse() {
  // Seul le propriétaire peut créer une dépense diverse (writeOwner côté back).
  // L'admin et l'employé voient la caisse en lecture seule.
  const { user } = useAuth();
  const peutSaisirDepense = user?.role === 'proprietaire';

  const today = new Date().toISOString().split('T')[0];
  const [operations, setOperations] = useState([]);
  const [totaux, setTotaux]         = useState(null);
  const [solde, setSolde]           = useState(null);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [modalDepense, setModalDepense] = useState(false);

  // Filtres
  const [dateDebut, setDateDebut] = useState(today);
  const [dateFin, setDateFin]     = useState(today);
  const [canal, setCanal]         = useState('tous');
  const [type, setType]           = useState('tous');

  // Les presets (Aujourd'hui/Hier/7j/30j/Ce mois/Mois dernier) sont fournis
  // par DateRangePicker directement — pas besoin d'un PeriodPills séparé.
  const resetToToday = () => {
    const t = getPeriodRange('today');
    setDateDebut(t.from); setDateFin(t.to);
    setCanal('tous'); setType('tous');
  };

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_debut: dateDebut, date_fin: dateFin, page, limit: 50 });
      if (canal !== 'tous') params.append('canal', canal);
      if (type  !== 'tous') params.append('type', type);

      const [{ data }, { data: soldeData }] = await Promise.all([
        api.get(`/caisse?${params}`),
        api.get('/caisse/solde'),
      ]);
      setOperations(data.operations || []);
      setTotaux(data.totaux || null);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setSolde(soldeData);
    } catch { toast.error('Erreur chargement caisse'); }
    finally { setLoading(false); }
  }, [dateDebut, dateFin, canal, type, page]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { setPage(1); }, [dateDebut, dateFin, canal, type]);

  return (
    <div className="flex flex-col gap-4">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white">Caisse</h1>
          <p className="text-gray-400 text-sm">Journal de trésorerie</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetToToday}
            className="p-2.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-xl transition-all"
            title="Actualiser (remet à Aujourd'hui)"
          >
            <RefreshCw size={16} />
          </button>
          {peutSaisirDepense && (
            <button onClick={() => setModalDepense(true)}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold px-4 py-2.5 rounded-xl transition-all">
              <Plus size={18} /> Dépense diverse
            </button>
          )}
        </div>
      </div>

      {/* KPI solde global — vue HONNÊTE de la trésorerie
          (cf. CLAUDE.md : appro = investissement stock, PAS une dépense) */}
      {solde && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Encaissements */}
            <div
              className="bg-gray-800/60 border border-gray-700 rounded-2xl p-4"
              title="Total des entrées d'argent : ventes magasin, livraisons, retours vendeurs, remboursements."
            >
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="text-green-400" size={18} />
                <p className="text-xs text-gray-400">Encaissements</p>
              </div>
              <p className="text-green-400 font-black text-lg lg:text-xl">{formatGNF(solde.encaissements)}</p>
            </div>

            {/* Dépenses opérationnelles */}
            <div
              className="bg-gray-800/60 border border-gray-700 rounded-2xl p-4"
              title="Sorties d'argent qui ne reviennent pas : carburant, salaires, internet, charges. N'inclut PAS les achats de stock."
            >
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="text-red-400" size={18} />
                <p className="text-xs text-gray-400">Dépenses opérationnelles</p>
              </div>
              <p className="text-red-400 font-black text-lg lg:text-xl">{formatGNF(solde.depenses_operationnelles)}</p>
            </div>

            {/* Investissement stock */}
            <div
              className="bg-gray-800/60 border border-gray-700 rounded-2xl p-4"
              title="Achats fournisseurs : c'est un investissement en marchandises, PAS une perte. Le stock acheté vaut cet argent et sera reconverti en cash quand il sera vendu."
            >
              <div className="flex items-center gap-2 mb-1">
                <Package className="text-orange-400" size={18} />
                <p className="text-xs text-gray-400">Investissement stock</p>
                <Info size={12} className="text-gray-500" />
              </div>
              <p className="text-orange-400 font-black text-lg lg:text-xl">{formatGNF(solde.investissement_stock)}</p>
            </div>

            {/* Trésorerie nette */}
            <div
              className={`border rounded-2xl p-4 ${
                Number(solde.tresorerie_nette) >= 0
                  ? 'bg-yellow-500/10 border-yellow-500/40'
                  : 'bg-red-900/20 border-red-500/40'
              }`}
              title="Encaissements − Dépenses opérationnelles − Investissement stock − Autres sorties. Représente l'argent net en caisse. Un solde négatif est NORMAL pendant une phase d'investissement stock — le stock acheté vaut cet argent."
            >
              <div className="flex items-center gap-2 mb-1">
                <Wallet
                  className={Number(solde.tresorerie_nette) >= 0 ? 'text-yellow-400' : 'text-red-400'}
                  size={18}
                />
                <p className="text-xs text-gray-400">Trésorerie nette</p>
                <Info size={12} className="text-gray-500" />
              </div>
              <p className={`font-black text-lg lg:text-xl ${
                Number(solde.tresorerie_nette) >= 0 ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {formatGNF(solde.tresorerie_nette)}
              </p>
            </div>
          </div>

          {/* Mini-explication pédagogique (affichée si trésorerie négative) */}
          {Number(solde.tresorerie_nette) < 0 && Number(solde.investissement_stock) > 0 && (
            <div className="flex items-start gap-2 px-4 py-2.5 bg-orange-900/20 border border-orange-700/40 rounded-xl text-xs text-orange-200">
              <Info size={14} className="text-orange-400 flex-shrink-0 mt-0.5" />
              <span>
                Trésorerie négative <strong>normale</strong> ici : tu as investi <strong>{formatGNF(solde.investissement_stock)}</strong> en
                stock fournisseurs. Cet argent n'est pas perdu — il dort dans tes marchandises
                et reviendra à mesure que tu vendras. Consulte la <strong>valeur de ton stock</strong> sur la page Stock pour confirmer.
              </span>
            </div>
          )}
        </>
      )}

      {/* Filtres */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <span className="text-xs text-gray-400 font-medium">Filtres</span>
        </div>
        <DateRangePicker
          debut={dateDebut}
          fin={dateFin}
          onChange={(d, f) => { setDateDebut(d); setDateFin(f); }}
        />
        <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Canal</label>
            <select value={canal} onChange={e => setCanal(e.target.value)}
              className="w-full bg-gray-900 border border-gray-600 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-yellow-500">
              <option value="tous">Tous</option>
              {Object.entries(CANAUX_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select value={type} onChange={e => setType(e.target.value)}
              className="w-full bg-gray-900 border border-gray-600 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-yellow-500">
              <option value="tous">Tous</option>
              <option value="entree">Entrées</option>
              <option value="sortie">Sorties</option>
            </select>
          </div>
        </div>

        {/* Totaux de la période */}
        {totaux && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t border-gray-700">
            <div className="text-center">
              <p className="text-xs text-gray-500">Entrées période</p>
              <p className="text-green-400 font-bold text-sm">{formatGNF(totaux.entrees)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Sorties période</p>
              <p className="text-red-400 font-bold text-sm">{formatGNF(totaux.sorties)}</p>
            </div>
            <div
              className="text-center"
              title="Encaissements − Dépenses op − Investissement stock − Autres sorties sur la période sélectionnée."
            >
              <p className="text-xs text-gray-500">Trésorerie nette</p>
              <p className={`font-bold text-sm ${Number(totaux.tresorerie_nette_periode) >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatGNF(totaux.tresorerie_nette_periode)}
              </p>
            </div>
            <div className="text-center" title="Différence brute entre toutes les entrées et toutes les sorties, sans distinction.">
              <p className="text-xs text-gray-500">Solde brut</p>
              <p className={`font-bold text-sm ${Number(totaux.solde_periode) >= 0 ? 'text-yellow-400' : 'text-gray-300'}`}>
                {formatGNF(totaux.solde_periode)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Opérations</p>
              <p className="text-white font-bold text-sm">{total}</p>
            </div>
          </div>
        )}
        {/* Détail par canal */}
        {totaux && (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-3 pt-3 border-t border-gray-700/50">
            {[
              { label: 'CA Magasin', val: totaux.ca_magasin, color: 'text-blue-400' },
              { label: 'CA Terrain', val: totaux.ca_terrain, color: 'text-purple-400' },
              { label: 'CA Livraison', val: totaux.ca_livraison, color: 'text-indigo-400' },
              { label: 'Invest. stock', val: totaux.achats_fournisseurs, color: 'text-orange-400' },
              { label: 'Dépenses op.', val: totaux.depenses_diverses, color: 'text-red-400' },
            ].map(item => (
              <div key={item.label} className="text-center">
                <p className="text-xs text-gray-600">{item.label}</p>
                <p className={`text-xs font-bold ${item.color}`}>{formatGNF(item.val)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Journal */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : operations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-600">
          <Wallet size={36} className="mb-3 opacity-40" />
          <p>Aucune opération pour cette période</p>
        </div>
      ) : (
        <div className="bg-gray-800/50 border border-gray-700 rounded-2xl overflow-auto max-h-[70vh]">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-900 text-xs text-gray-400 uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left">Date / Heure</th>
                <th className="px-4 py-3 text-left">Canal</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-left">Par</th>
                <th className="px-4 py-3 text-right">Montant</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {operations.map(op => (
                <tr key={op.id} className="hover:bg-gray-700/20 transition-colors">
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    <div>{new Date(op.date).toLocaleDateString('fr-FR')}</div>
                    <div className="text-xs text-gray-600">{op.heure?.slice(0,5)}</div>
                  </td>
                  <td className="px-4 py-3"><CanalBadge canal={op.canal} /></td>
                  <td className="px-4 py-3 text-gray-200 max-w-xs truncate">{op.description}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{op.user_nom}</td>
                  <td className={`px-4 py-3 text-right font-bold whitespace-nowrap ${op.type === 'entree' ? 'text-green-400' : 'text-red-400'}`}>
                    {op.type === 'entree' ? '+' : '-'}{formatGNF(op.montant)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl disabled:opacity-40 text-sm hover:bg-gray-700">← Préc.</button>
          <span className="px-4 py-2 text-gray-400 text-sm">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl disabled:opacity-40 text-sm hover:bg-gray-700">Suiv. →</button>
        </div>
      )}

      {/* Modal dépense */}
      {modalDepense && <ModalDepense onClose={() => setModalDepense(false)} onSuccess={() => { setModalDepense(false); charger(); }} />}
    </div>
  );
}
